import { isEmpty, isNull } from 'lodash';
import { FixedNavItems, NavItems } from './nav-items';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import { leadFinderViewOnly } from '../../../../../shared/utils/user-details';

type FilterNavItemsProps = {
  isAgency: number;
  client: Record<string, any>;
  isMultichannelActive: boolean;
  isSaleshandyCopilotEnabled: boolean;
  is1To1EmailInsightsEnabled: boolean;
  hasDomains: boolean;
  hasDedicatedIps: boolean;
  leadFinderVersion?: 'v1' | 'v2' | null;
  isDialerFeatureEnabled: boolean;
  isAgencyUser: boolean;
};

export const filterNavItems = ({
  isAgency,
  client,
  isMultichannelActive,
  isSaleshandyCopilotEnabled,
  is1To1EmailInsightsEnabled,
  hasDomains,
  hasDedicatedIps,
  leadFinderVersion,
  isDialerFeatureEnabled,
  isAgencyUser,
}: FilterNavItemsProps) => {
  const mappedNavItems = NavItems.map((item) => {
    if (item.key === 'billing') {
      return {
        ...item,
        text: hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)
          ? 'Billing & Subscription'
          : 'Plan details & Billing',
      };
    }
    // Update leads route based on version
    if (item.key === 'leads') {
      return {
        ...item,
        route: leadFinderVersion === 'v2' ? '/v2/leads' : '/leads',
      };
    }
    return item;
  });

  const tempFixedNavItems = FixedNavItems.filter((item) => {
    // AL-3690: hide '1:1 Email Insights' tab for accounts with no recent
    // (≤60d) 1:1 sent activity. BE-driven flag from GET /meta.
    if (item.key === 'email-insights' && !is1To1EmailInsightsEnabled) {
      return false;
    }
    return typeof item.permission === 'function'
      ? item.permission()
      : hasPermission(item.permission);
  });

  const tempBottomNavItems = [];

  let tempNavItems = mappedNavItems.filter((item) => {
    // Show 'leads' tab for agency-client users for specific shAccountId
    if (
      item.key === 'leads' &&
      !isNull(client) &&
      !isEmpty(client) &&
      leadFinderViewOnly()
    ) {
      return true;
    }

    if (
      typeof item.permission === 'function'
        ? item.permission()
        : hasPermission(item.permission)
    ) {
      // If the item is 'agency-client' and the user is not an agency,
      // then move it to fixed nav items and exclude from main nav
      if (item.key === 'agency-client' && !isAgency) {
        tempFixedNavItems.unshift(item);
        return false;
      }

      // Exclude the 'tasks' nav item if multichannel is not active
      if (item.key === 'tasks' && !isMultichannelActive) {
        return false;
      }

      // Exclude the 'copilot' nav item if Saleshandy Copilot is not enabled
      if (item.key === 'copilot' && !isSaleshandyCopilotEnabled) {
        return false;
      }

      if (item.key === 'dialer' && (!isDialerFeatureEnabled || isAgencyUser)) {
        return false;
      }

      // Move 'settings' nav item to bottom if user is not an agency user
      if (item.key === 'setting' && (isNull(client) || isEmpty(client))) {
        tempBottomNavItems.push(item);
        return false;
      }

      return true;
    }

    return false;
  });

  tempNavItems = tempNavItems.map((item) => {
    if (item?.subNavItems?.length) {
      const tempItems = { ...item };

      const tempSubNavItems = item.subNavItems.filter((obj) => {
        if (item.key === 'email-accounts') {
          if (obj.key === 'domains' && !hasDomains) {
            return false;
          }
          if (obj.key === 'inframail-ips' && !hasDedicatedIps) {
            return false;
          }
        }
        return true;
      });

      tempItems.subNavItems = tempSubNavItems;

      return tempItems;
    }

    return item;
  });

  return {
    filteredNavItems: tempNavItems,
    filteredFixedNavItems: tempFixedNavItems,
    filteredBottomNavItems: tempBottomNavItems,
  };
};
